import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, ChevronRight, Link as LinkIcon, ChevronLeft } from 'lucide-react';

const ActualiteCard = ({ item }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const parseMarkdown = (text) => {
    if (!text) return ''

    // Échapper les caractères HTML
    let html = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')

    // Nettoyer les URLs malformées (espaces après //)
    html = html.replace(/!\[([^\]]*)\]\(https:\s+\/\//g, '![$1](https://')
    html = html.replace(/!\[([^\]]*)\]\(http:\s+\/\//g, '![$1](http://')

    // Splitter par lignes
    const lines = html.split('\n')
    const result = []
    let i = 0

    while (i < lines.length) {
      const line = lines[i].trim()

      if (!line) {
        i++
        continue
      }

      // Titres
      if (/^# /.test(line)) {
        result.push(`<h1 class="text-3xl font-bold text-white mt-8 mb-4">${line.replace(/^# /, '')}</h1>`)
        i++
        continue
      }
      if (/^## /.test(line)) {
        result.push(`<h2 class="text-2xl font-bold text-white mt-6 mb-3">${line.replace(/^## /, '')}</h2>`)
        i++
        continue
      }
      if (/^### /.test(line)) {
        result.push(`<h3 class="text-xl font-semibold text-gray-100 mt-4 mb-2">${line.replace(/^### /, '')}</h3>`)
        i++
        continue
      }
      if (/^#### /.test(line)) {
        result.push(`<h4 class="text-lg font-semibold text-gray-200 mt-3 mb-2">${line.replace(/^#### /, '')}</h4>`)
        i++
        continue
      }

      // Listes à puces
      if (/^[\-\*\+] /.test(line)) {
        const listItems = []
        while (i < lines.length && /^[\-\*\+] /.test(lines[i].trim())) {
          listItems.push(`<li class="ml-6 text-gray-200">${lines[i].trim().replace(/^[\-\*\+] /, '')}</li>`)
          i++
        }
        result.push(`<ul class="list-disc space-y-1 my-3">${listItems.join('')}</ul>`)
        continue
      }

      // Listes numérotées
      if (/^\d+\. /.test(line)) {
        const listItems = []
        while (i < lines.length && /^\d+\. /.test(lines[i].trim())) {
          listItems.push(`<li class="ml-6 text-gray-200">${lines[i].trim().replace(/^\d+\. /, '')}</li>`)
          i++
        }
        result.push(`<ol class="list-decimal space-y-1 my-3">${listItems.join('')}</ol>`)
        continue
      }

      // Citations
      if (/^> /.test(line)) {
        const quotes = []
        while (i < lines.length && /^> /.test(lines[i].trim())) {
          quotes.push(lines[i].trim().replace(/^> /, ''))
          i++
        }
        result.push(`<blockquote class="border-l-4 border-green-500 pl-4 italic text-gray-300 my-2">${quotes.join('<br />')}</blockquote>`)
        continue
      }

      // Bloc de code
      if (/^```/.test(line)) {
        const codeLines = []
        i++
        while (i < lines.length && !/^```/.test(lines[i])) {
          codeLines.push(lines[i])
          i++
        }
        result.push(`<pre class="bg-slate-900 border border-slate-600 rounded-lg p-4 my-3 overflow-x-auto"><code class="text-gray-200 font-mono text-sm">${codeLines.join('\n')}</code></pre>`)
        i++
        continue
      }

      // Ligne horizontale
      if (/^[\-\*_]{3,}$/.test(line)) {
        result.push('<hr class="border-t border-slate-600 my-4" />')
        i++
        continue
      }

      // Paragraphes normaux et images - grouper les lignes consécutives
      const paragraphLines = []
      while (i < lines.length && lines[i].trim() && 
             !/^[#]{1,4} /.test(lines[i].trim()) &&
             !/^[\-\*\+] /.test(lines[i].trim()) &&
             !/^\d+\. /.test(lines[i].trim()) &&
             !/^> /.test(lines[i].trim()) &&
             !/^```/.test(lines[i].trim()) &&
             !/^[\-\*_]{3,}$/.test(lines[i].trim())) {
        paragraphLines.push(lines[i])
        i++
      }

      if (paragraphLines.length > 0) {
        let content = paragraphLines.join('\n')

        // Séparer le contenu et les images
        const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g
        let lastIndex = 0
        const parts = []
        let match

        while ((match = imageRegex.exec(content)) !== null) {
          // Ajouter le texte avant l'image
          if (match.index > lastIndex) {
            let textBefore = content.substring(lastIndex, match.index).trim()
            if (textBefore) {
              parts.push({ type: 'text', content: textBefore })
            }
          }
          // Ajouter l'image
          parts.push({ type: 'image', alt: match[1], src: match[2].trim() })
          lastIndex = match.index + match[0].length
        }

        // Ajouter le texte restant
        if (lastIndex < content.length) {
          let textAfter = content.substring(lastIndex).trim()
          if (textAfter) {
            parts.push({ type: 'text', content: textAfter })
          }
        }

        // Si pas d'images, traiter comme avant
        if (parts.length === 0) {
          parts.push({ type: 'text', content: content })
        }

        // Rendu des parties
        parts.forEach(part => {
          if (part.type === 'image') {
            result.push(`<img src="${part.src}" alt="${part.alt}" class="w-full max-w-2xl h-auto rounded-lg my-4 border border-slate-600" />`)
          } else {
            let formattedContent = part.content

            // Code inline
            formattedContent = formattedContent.replace(/`([^`]+)`/g, '<code class="bg-slate-900 text-orange-400 px-2 py-1 rounded font-mono text-sm">$1</code>')

            // Gras
            formattedContent = formattedContent.replace(/\*\*([^\*]+)\*\*/g, '<strong class="font-bold text-white">$1</strong>')
            formattedContent = formattedContent.replace(/__([^_]+)__/g, '<strong class="font-bold text-white">$1</strong>')

            // Italique
            formattedContent = formattedContent.replace(/\*([^\*]+)\*/g, '<em class="italic text-gray-300">$1</em>')
            formattedContent = formattedContent.replace(/_([^_]+)_/g, '<em class="italic text-gray-300">$1</em>')

            // Liens
            formattedContent = formattedContent.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-green-400 hover:text-green-300 underline">$1</a>')

            // Sauts de ligne
            formattedContent = formattedContent.replace(/\n/g, '<br />')

            result.push(`<p class="text-gray-200 leading-relaxed">${formattedContent}</p>`)
          }
        })
      }
    }

    return result.join('')
  }

  const nextImage = () => {
    if (item.additionalImages && item.additionalImages.length > 0) {
      setCurrentImageIndex((prev) => 
        prev === item.additionalImages.length - 1 ? 0 : prev + 1
      );
    }
  };

  const previousImage = () => {
    if (item.additionalImages && item.additionalImages.length > 0) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? item.additionalImages.length - 1 : prev - 1
      );
    }
  };

  return (
    <motion.div
      className="relative h-[600px] w-full"
      initial={false}
      animate={{ rotateY: isFlipped ? 180 : 0 }}
      transition={{ duration: 0.6 }}
      style={{ transformStyle: 'preserve-3d' }}
    >
      {/* Face avant */}
      <div
        className={`absolute w-full h-full bg-slate-800/60 rounded-lg overflow-hidden backdrop-blur-sm ${
          isFlipped ? 'hidden' : ''
        }`}
        style={{
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
        }}
      >
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-48 object-cover"
        />
        <div className="p-6">
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
            <Calendar className="w-4 h-4" />
            {new Date(item.date).toLocaleDateString()}
          </div>
          <span className="inline-block bg-green-600/20 text-green-400 text-sm px-3 py-1 rounded-full mb-3">
            {item.category}
          </span>
          <h2 className="text-xl font-semibold text-white mb-2">
            {item.title}
          </h2>
          <p className="text-gray-300 mb-4">
            {item.description}
          </p>
          <button
            onClick={() => setIsFlipped(true)}
            className="flex items-center text-green-400 hover:text-green-300 transition-colors"
          >
            Lire la suite
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div>
      </div>

      {/* Face arrière */}
      <div
        className={`absolute w-full h-full bg-slate-800/60 rounded-lg overflow-hidden backdrop-blur-sm ${
          !isFlipped ? 'hidden' : ''
        }`}
        style={{
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          transform: 'rotateY(180deg)',
        }}
      >
        <div className="p-6 overflow-y-auto h-full">
          <h3 className="text-xl font-semibold text-white mb-4">{item.title}</h3>
          
          {/* Afficher le contenu markdown parsé */}
          <div 
            className="prose prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: parseMarkdown(item.detailedDescription) }}
          />

          {/* Galerie d'images */}
          {item.additionalImages && item.additionalImages.length > 0 && (
            <div className="mt-6 mb-4">
              <div className="relative">
                <img
                  src={item.additionalImages[currentImageIndex]}
                  alt={`Image ${currentImageIndex + 1}`}
                  className="w-full h-64 object-cover rounded-lg"
                />
                {item.additionalImages.length > 1 && (
                  <>
                    <button
                      onClick={previousImage}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 p-2 rounded-full hover:bg-black/75 transition-colors"
                    >
                      <ChevronLeft className="w-6 h-6 text-white" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 p-2 rounded-full hover:bg-black/75 transition-colors"
                    >
                      <ChevronRight className="w-6 h-6 text-white" />
                    </button>
                  </>
                )}
              </div>
              <div className="flex justify-center gap-2 mt-2">
                {item.additionalImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      currentImageIndex === index ? 'bg-green-500' : 'bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
          
          {item.websiteUrl && (
            <a
              href={item.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 mb-4"
            >
              <LinkIcon className="w-4 h-4" />
              En savoir plus
            </a>
          )}
          
          <button
            onClick={() => setIsFlipped(false)}
            className="flex items-center text-green-400 hover:text-green-300 transition-colors mt-4"
          >
            <ChevronRight className="w-4 h-4 mr-1 rotate-180" />
            Retour à l'article
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ActualiteCard;